import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp, orderBy
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import Webcam from "react-webcam";
import Swal from 'sweetalert2';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, Divider, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid, MenuItem, Autocomplete, Stack, Chip
} from '@mui/material';
import {
    Delete as DeleteIcon, Search as SearchIcon, Add as AddIcon,
    Close as CloseIcon, CameraAlt as CameraIcon, Autorenew as RetakeIcon,
    Visibility as ViewIcon, Edit as EditIcon, AccountBalanceWallet
} from '@mui/icons-material';

const Hospitalbill = () => {
    // Data & Core States
    const [hospitalBills, setHospitalBills] = useState([]);
    const [students, setStudents] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [viewPhoto, setViewPhoto] = useState(null);
    const [openPhotoModal, setOpenPhotoModal] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [existingRecord, setExistingRecord] = useState(null); // Track original values for edit diffing

    // Form Field States
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [billNo, setBillNo] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [hospitalName, setHospitalName] = useState("");
    const [diagnosis, setDiagnosis] = useState("");
    const [hospitalFee, setHospitalFee] = useState("");
    const [medicalFee, setMedicalFee] = useState("");
    const [paidAmount, setPaidAmount] = useState("");
    const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
    const [note, setNote] = useState("");
    const [paymentSource, setPaymentSource] = useState('Manual'); // Manual or Wallet
    const [payMode, setPayMode] = useState('Cash');

    // Calculated fields
    const totalAmount = (parseFloat(hospitalFee) || 0) + (parseFloat(medicalFee) || 0);
    const remainingAmount = totalAmount - (parseFloat(paidAmount) || 0);

    // Webcam State
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [showCamera, setShowCamera] = useState(false);

    // Table Pagination & Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userSnap = await getDoc(doc(db, "users", user.uid));
                if (userSnap.exists()) {
                    const sId = userSnap.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Fetch Wallet Balance when student changes
    useEffect(() => {
        if (selectedStudent?.studentId) {
            fetchWalletBalance(selectedStudent.studentId);
        } else {
            setWalletBalance(0);
        }
    }, [selectedStudent]);

    const fetchWalletBalance = async (studentId) => {
        try {
            const q = query(collection(db, "wallet_transactions"), where("studentId", "==", studentId));
            const snap = await getDocs(q);
            const bal = snap.docs.reduce((acc, curr) =>
                curr.data().type === 'Credit' ? acc + Number(curr.data().amount) : acc - Number(curr.data().amount), 0);
            setWalletBalance(bal);
        } catch (error) {
            console.error("Error fetching wallet balance:", error);
        }
    };

    const fetchInitialData = async (sId) => {
        // Fetch Active Students
        const admSnap = await getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("status", "==", "Active")));
        const list = await Promise.all(admSnap.docs.map(async (adm) => {
            const admData = adm.data();
            const sSnap = await getDoc(doc(db, "students", admData.studentId));
            const sData = sSnap.data();
            return {
                id: adm.id,
                studentId: admData.studentId,
                label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase().trim(),
            };
        }));
        setStudents(list);

        fetchHospitalBills(sId);
    };

    const fetchHospitalBills = async (sId) => {
        try {
            const q = query(collection(db, "hospitalBills"), where("schoolId", "==", sId), orderBy("updatedAt", "desc"));
            const snap = await getDocs(q);
            setHospitalBills(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error fetching hospital bills:", error);
        }
    };

    const capture = useCallback(() => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            setImgSrc(imageSrc);
            setShowCamera(false);
        }
    }, [webcamRef]);

    const handleEdit = (row) => {
        setEditId(row.id);
        setExistingRecord(row);

        // Match selected student from array
        const matchedStudent = students.find(s => s.id === row.studentAdmissionId) || {
            id: row.studentAdmissionId,
            studentId: row.studentId,
            label: row.studentName
        };
        setSelectedStudent(matchedStudent);

        setBillNo(row.billNo || '');
        setDate(row.date || new Date().toISOString().split('T')[0]);
        setHospitalName(row.hospitalName || '');
        setDiagnosis(row.diagnosis || '');
        setHospitalFee(row.hospitalFee || '');
        setMedicalFee(row.medicalFee || '');
        setPaidAmount(row.paidAmount || '');
        setPaidDate(row.paidDate || new Date().toISOString().split('T')[0]);
        setNote(row.note || '');
        setPaymentSource(row.paymentSource || (row.payMode === 'Wallet' ? 'Wallet' : 'Manual'));
        setPayMode(row.payMode || 'Cash');
        setImgSrc(row.photoUrl || null);

        setOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) return Swal.fire("Error", "Please select a student", "error");

        const paid = parseFloat(paidAmount) || 0;

        // Validation for new wallet payments or increasing amount on edits
        if (paymentSource === 'Wallet') {
            const previousPaid = (editId && existingRecord?.payMode === 'Wallet') ? Number(existingRecord.paidAmount) : 0;
            const additionalRequired = paid - previousPaid;

            if (additionalRequired > 0 && walletBalance < additionalRequired) {
                return Swal.fire("Insufficient Balance", `Available wallet balance is ₹${walletBalance}, but ₹${additionalRequired} more is required.`, "warning");
            }
        }

        setSaving(true);
        try {
            let photoUrl = imgSrc || null;
            if (imgSrc && imgSrc.startsWith('data:image')) {
                const storageRef = ref(storage, `hospital_bills/${schoolId}/${Date.now()}.jpg`);
                await uploadString(storageRef, imgSrc, 'data_url');
                photoUrl = await getDownloadURL(storageRef);
            }

            const payload = {
                studentAdmissionId: selectedStudent.id,
                studentId: selectedStudent.studentId,
                studentName: selectedStudent.label,
                billNo,
                date,
                hospitalName,
                diagnosis,
                hospitalFee: parseFloat(hospitalFee) || 0,
                medicalFee: parseFloat(medicalFee) || 0,
                totalAmount,
                paidAmount: paid,
                remainingAmount,
                paidDate,
                note,
                paymentSource,
                payMode: paymentSource === 'Wallet' ? 'Wallet' : payMode,
                photoUrl,
                schoolId,
                updatedAt: serverTimestamp()
            };

            if (editId) {
                await updateDoc(doc(db, "hospitalBills", editId), payload);

                // Adjust Wallet Transaction on Edit if mode is Wallet
                if (paymentSource === 'Wallet') {
                    const prevPaid = Number(existingRecord?.paidAmount) || 0;
                    const diff = paid - prevPaid;

                    if (diff > 0) {
                        // Extra amount paid -> Debit difference
                        await addDoc(collection(db, "wallet_transactions"), {
                            studentId: selectedStudent.studentId,
                            amount: diff,
                            type: 'Debit',
                            description: `Updated Hospital Bill #${billNo} (Additional Fee)`,
                            timestamp: serverTimestamp(),
                            dateTime: new Date().toLocaleString()
                        });
                    } else if (diff < 0) {
                        // Amount reduced -> Credit difference back
                        await addDoc(collection(db, "wallet_transactions"), {
                            studentId: selectedStudent.studentId,
                            amount: Math.abs(diff),
                            type: 'Credit',
                            description: `Updated Hospital Bill #${billNo} (Fee Reduced)`,
                            timestamp: serverTimestamp(),
                            dateTime: new Date().toLocaleString()
                        });
                    }
                }
            } else {
                await addDoc(collection(db, "hospitalBills"), { ...payload, createdAt: serverTimestamp() });

                // New Wallet Deduction
                if (paymentSource === 'Wallet' && paid > 0) {
                    await addDoc(collection(db, "wallet_transactions"), {
                        studentId: selectedStudent.studentId,
                        amount: paid,
                        type: 'Debit',
                        description: `Hospital Bill (${hospitalName || 'Medical Fee'}): Bill #${billNo}`,
                        timestamp: serverTimestamp(),
                        dateTime: new Date().toLocaleString()
                    });
                }
            }

            Swal.fire("Success", `Hospital bill record ${editId ? 'updated' : 'saved'} successfully`, "success");
            handleClose();
            fetchHospitalBills(schoolId);
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to save record", "error");
        }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setExistingRecord(null);
        setSelectedStudent(null);
        setBillNo('');
        setDate(new Date().toISOString().split('T')[0]);
        setHospitalName('');
        setDiagnosis('');
        setHospitalFee('');
        setMedicalFee('');
        setPaidAmount('');
        setPaidDate(new Date().toISOString().split('T')[0]);
        setNote('');
        setPaymentSource('Manual');
        setPayMode('Cash');
        setImgSrc(null);
        setShowCamera(false);
    };

    const handleDelete = async (row) => {
        if (window.confirm("Are you sure you want to delete this bill record?")) {
            try {
                // Restore Wallet Balance if paid via wallet
                if (row.payMode === 'Wallet' && row.paidAmount > 0) {
                    await addDoc(collection(db, "wallet_transactions"), {
                        studentId: row.studentId,
                        amount: row.paidAmount,
                        type: 'Credit',
                        description: `Restored: Deleted Hospital Bill #${row.billNo}`,
                        timestamp: serverTimestamp(),
                        dateTime: new Date().toLocaleString()
                    });
                }

                await deleteDoc(doc(db, "hospitalBills", row.id));
                fetchHospitalBills(schoolId);

                if (selectedStudent?.studentId === row.studentId) {
                    fetchWalletBalance(row.studentId);
                }
                Swal.fire("Deleted", "Record has been deleted.", "success");
            } catch (error) {
                console.error("Error deleting record:", error);
                Swal.fire("Error", "Could not delete record.", "error");
            }
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };

    const filtered = hospitalBills.filter(i =>
        (i.studentName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (i.hospitalName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (i.billNo?.toLowerCase() || "").includes(searchTerm.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    return (
        <Grid  sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} md={11}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" fontWeight="700">Hospital Bills</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                            New Bill Entry
                        </Button>
                    </Stack>

                    <TextField
                        size="small"
                        placeholder="Search student, hospital, or bill no..."
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 3, maxWidth: 400 }}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
                    />

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell>Sr. No.</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Bill No</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Hospital</TableCell>
                                    <TableCell>Diagnosis</TableCell>
                                    <TableCell>Total Amount</TableCell>
                                    <TableCell>Paid / Balance</TableCell>
                                    <TableCell>Payment Mode</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                            No hospital bills found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((row, index) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>
                                            <TableCell>{formatDate(row.date)}</TableCell>
                                            <TableCell>{row.billNo}</TableCell>
                                            <TableCell>{row.studentName}</TableCell>
                                            <TableCell>{row.hospitalName}</TableCell>
                                            <TableCell>{row.diagnosis}</TableCell>
                                            <TableCell>₹{row.totalAmount}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                                    Paid: ₹{row.paidAmount}
                                                </Typography>
                                                {row.remainingAmount > 0 && (
                                                    <Typography variant="caption" color="error">
                                                        Bal: ₹{row.remainingAmount}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.payMode}
                                                    size="small"
                                                    color={row.payMode === 'Wallet' ? 'secondary' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.photoUrl && (
                                                    <IconButton
                                                        onClick={() => {
                                                            setViewPhoto(row.photoUrl);
                                                            setOpenPhotoModal(true);
                                                        }}
                                                        color="info"
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <IconButton onClick={() => handleEdit(row)} color="primary">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete(row)} color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filtered.length > itemsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={Math.ceil(filtered.length / itemsPerPage)}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </Paper>
            </Grid>

            {/* Entry / Edit Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editId ? "Edit Hospital Bill" : "Hospital Bill Entry"}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} className="col-sm-6 mb-2">
                                <Autocomplete
                                    options={students}
                                    getOptionLabel={(o) => o.label || ""}
                                    value={selectedStudent}
                                    onChange={(e, v) => setSelectedStudent(v)}
                                    renderInput={(params) => <TextField {...params} label="Select Student" size="small" required />}
                                />
                            </Grid>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField fullWidth label="Bill Date" type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
                            </Grid>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField fullWidth label="Bill No" size="small" value={billNo} onChange={(e) => setBillNo(e.target.value)} required />
                            </Grid>
                        </Grid>

                        <Grid className="row"   sx={{ mb: 2 }}>
                            <Grid item xs={12} className="col-sm-6 mb-2">
                                <TextField fullWidth label="Hospital Name" size="small" value={hospitalName} onChange={(e) => setHospitalName(e.target.value)} required />
                            </Grid>
                            <Grid item xs={12} className="col-sm-6 mb-2">
                                <TextField fullWidth label="Diagnosis / Disease" size="small" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 2 }}>
                            <Paper sx={{ p: 1.5, bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', border: '1px dashed green' }}>
                                <AccountBalanceWallet sx={{ color: 'green', mr: 1 }} />
                                <Typography variant="subtitle2">
                                    Current Student Wallet Balance: <b>₹{walletBalance}</b>
                                </Typography>
                            </Paper>
                        </Box>

                        <Typography variant="overline" color="textSecondary" fontWeight="bold">Bill Details</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid className="row" spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} className="col-sm-4 mb-2">
                                <TextField
                                    fullWidth
                                    label="Hospital Fee (₹)"
                                    type="number"
                                    size="small"
                                    value={hospitalFee}
                                    onChange={(e) => setHospitalFee(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} className="col-sm-4 mb-2">
                                <TextField
                                    fullWidth
                                    label="Medical / Medicine Fee (₹)"
                                    type="number"
                                    size="small"
                                    value={medicalFee}
                                    onChange={(e) => setMedicalFee(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} className="col-sm-4 mb-2">
                                <TextField
                                    fullWidth
                                    label="Total Amount (₹)"
                                    type="number"
                                    size="small"
                                    value={totalAmount}
                                    disabled
                                    sx={{ bgcolor: '#f8fafc' }}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="overline" color="textSecondary" fontWeight="bold">Payment Information</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid className="row" spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField
                                    fullWidth
                                    label="Paid Amount (₹)"
                                    type="number"
                                    size="small"
                                    value={paidAmount}
                                    onChange={(e) => setPaidAmount(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField
                                    fullWidth
                                    label="Remaining Amount (₹)"
                                    type="number"
                                    size="small"
                                    value={remainingAmount}
                                    disabled
                                    error={remainingAmount > 0}
                                />
                            </Grid>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField fullWidth label="Paid Date" type="date" size="small" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} className="col-sm-3 mb-2">
                                <TextField select label="Pay From" size="small" fullWidth value={paymentSource} onChange={(e) => setPaymentSource(e.target.value)}>
                                    <MenuItem value="Manual">Manual (Cash/UPI)</MenuItem>
                                    <MenuItem value="Wallet">Student Wallet</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        {paymentSource === 'Manual' && (
                            <Grid className="row" spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} className="col-sm-3 mb-2">
                                    <TextField select label="Mode" size="small" fullWidth value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                                        <MenuItem value="Cash">Cash</MenuItem>
                                        <MenuItem value="UPI">Online / UPI</MenuItem>
                                        <MenuItem value="Card">Card</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        )}

                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-6" item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Note / Remarks"
                                    size="small"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            </Grid>
                        </Grid>

                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving} color={editId ? "primary" : "success"}>
                            {saving ? 'Processing...' : (editId ? 'Update Record' : 'Complete Transaction')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* View Photo Modal */}
            <Dialog open={openPhotoModal} onClose={() => setOpenPhotoModal(false)}>
                <DialogTitle>Bill Document Evidence</DialogTitle>
                <DialogContent>
                    <Box component="img" src={viewPhoto} alt="Bill Evidence" sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPhotoModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default Hospitalbill;